/**
 * Rollen & Rechte, Etappe 4: Reagiert auf den Header `X-Rechte-Stand`, den der Server an jede angemeldete Antwort hängt.
 *
 * Der erste gesehene Wert wird nur gemerkt. Ändert er sich, wird einmal nachgeladen (eigenes Konto und offene Helferliste) —
 * entprellt, nie parallel und nie in einer Schleife: Kommt während eines laufenden Nachladens eine weitere Änderung, wird sie
 * nur vorgemerkt und danach höchstens einmal nachgeholt. Schlägt das Nachladen fehl, folgt kein Wiederholen. Kein Neuladen der Seite.
 * Rein (ohne axios und React); die Anbindung steht in `api/api.ts` und `App.tsx` über ein Abo (Rückruf), nicht über Importe.
 */
export const RECHTE_STAND_HEADER = 'x-rechte-stand';

export interface RechteStandOptionen {
  /** Wartezeit, um mehrere Auslöser (Header, Live-Meldung) zu einem Nachladen zusammenzufassen */
  verzoegerungMs?: number;
}

export const erzeugeRechteStandWaechter = (opt: RechteStandOptionen = {}) => {
  const verzoegerung = opt.verzoegerungMs ?? 250;
  let gemerkt: string | null = null;
  let rueckruf: (() => Promise<void> | void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let laeuft = false;
  let vorgemerkt = false;

  const lauf = async (): Promise<void> => {
    timer = null;
    if (!rueckruf) return;
    if (laeuft) { vorgemerkt = true; return; }
    laeuft = true;
    try { await rueckruf(); } catch { /* kein Wiederholen: der nächste echte Wechsel löst wieder aus */ }
    laeuft = false;
    if (vorgemerkt) { vorgemerkt = false; anfordern(); }
  };

  /** Nachladen anfordern (entprellt; während eines Laufs nur vormerken) */
  const anfordern = (): void => {
    if (laeuft) { vorgemerkt = true; return; }
    if (timer) return;
    timer = setTimeout(() => { void lauf(); }, verzoegerung);
  };

  return {
    /** Wert des Headers einer Antwort; fehlt er oder ist er keine Zahl, geschieht nichts */
    meldeStand(roh: unknown): void {
      const text = Array.isArray(roh) ? roh[0] : roh;
      if (typeof text !== 'string' && typeof text !== 'number') return;
      const stand = String(text).trim();
      if (!/^\d+$/.test(stand)) return;
      if (gemerkt === null) { gemerkt = stand; return; }
      // Nur ein größerer Stand zählt: Der Server zählt hoch (auch über Neustarts); eine kleinere Zahl stammt aus einer veralteten
      // Antwort (Zwischenspeicher des Browsers) und darf nicht hin und her laden
      if (Number(stand) <= Number(gemerkt)) return;
      // Vorher setzen: die Antwort des Nachladens trägt schon den neuen Stand und löst nichts mehr aus
      gemerkt = stand;
      anfordern();
    },
    anfordern,
    /** Neue Sitzung (Anmelden, Abmelden): der nächste Wert ist wieder ein erster */
    zuruecksetzen(): void {
      gemerkt = null; vorgemerkt = false;
      if (timer) { clearTimeout(timer); timer = null; }
    },
    /** Rückruf anmelden (genau einer); die Rückgabe meldet ihn wieder ab */
    abonniere(f: () => Promise<void> | void): () => void {
      rueckruf = f;
      return () => { if (rueckruf === f) rueckruf = null; };
    },
    /** nur für Tests */
    gemerkterStand: (): string | null => gemerkt,
  };
};

export const rechteStand = erzeugeRechteStandWaechter();
